import { Link } from "react-router-dom";

export default function Security() {
  const securityFeatures = [
    {
      icon: "🔐",
      title: "End-to-End Encryption",
      description: "All data is encrypted using AES-256 encryption, the same standard used by banks and government agencies.",
      details: [
        "Data encrypted in transit and at rest",
        "Unique encryption keys for each account",
        "Regular encryption key rotation",
        "Zero-knowledge architecture"
      ]
    },
    {
      icon: "🛡️",
      title: "Secure Infrastructure", 
      description: "Hosted on enterprise-grade cloud infrastructure with 99.9% uptime and automatic security updates.",
      details: [
        "AWS/Google Cloud secure data centers",
        "Automatic security patches",
        "DDoS protection and monitoring",
        "Regular penetration testing"
      ]
    },
    {
      icon: "👤",
      title: "Access Controls",
      description: "Multi-layered authentication and authorization ensure only authorized users access student data.",
      details: [
        "Multi-factor authentication available",
        "Role-based access permissions",
        "Session timeout protection",
        "Audit logs for all access"
      ]
    },
    {
      icon: "📊",
      title: "Data Minimization",
      description: "We collect only the minimum data necessary for educational purposes and delete it when no longer needed.",
      details: [
        "No personal data collection from students",
        "Automatic data purging policies",
        "Anonymized analytics only",
        "Right to data deletion"
      ]
    }
  ];

  const compliance = [
    {
      title: "GDPR Compliant",
      badge: "🇪🇺 EU",
      description: "Full compliance with European General Data Protection Regulation",
      features: [
        "Right to access personal data",
        "Right to delete personal data",
        "Data portability for teachers",
        "Privacy by design architecture",
        "Regular compliance audits"
      ]
    },
    {
      title: "COPPA Compliant", 
      badge: "🇺🇸 US",
      description: "Certified for Children's Online Privacy Protection Act compliance",
      features: [
        "No collection of personal info from under-13s",
        "Parental consent mechanisms",
        "Student data anonymization",
        "Safe communication channels",
        "Regular COPPA compliance reviews"
      ]
    },
    {
      title: "FERPA Compliant",
      badge: "🏫 EDU", 
      description: "Meets Family Educational Rights and Privacy Act requirements",
      features: [
        "Educational record protection",
        "Controlled access to student data",
        "Audit trails for data access",
        "Data sharing restrictions",
        "Student rights protection"
      ]
    },
    {
      title: "SOC 2 Type II",
      badge: "✅ SEC",
      description: "Independently audited security and availability controls",
      features: [
        "Annual third-party audits",
        "Security control validation",
        "Availability monitoring",
        "Processing integrity checks",
        "Confidentiality measures"
      ]
    }
  ];

  const dataHandling = [
    {
      stage: "Collection",
      icon: "📥",
      description: "Minimal Data Collection",
      details: "We collect only essential information: teacher contact info, student names (no personal details), and exam responses. No behavioral tracking outside of cheating detection."
    },
    {
      stage: "Storage", 
      icon: "💾",
      description: "Secure Cloud Storage",
      details: "All data is encrypted and stored in certified data centers with physical security, biometric access, and 24/7 monitoring. Multiple geographic backups ensure reliability."
    },
    {
      stage: "Processing",
      icon: "⚙️", 
      description: "Purpose-Limited Processing",
      details: "Student data is used only for exam delivery, grading, and progress tracking. We never use student data for advertising or share it with third parties."
    },
    {
      stage: "Retention",
      icon: "📅",
      description: "Automatic Data Deletion",
      details: "Student data is automatically deleted after account closure or upon teacher request. Exam responses can be deleted immediately after grading if desired."
    }
  ];

  const privacy = [
    {
      title: "What We Collect",
      items: [
        "Teacher name and email address",
        "Student names (first name, last initial only)",
        "Exam questions and responses", 
        "Performance analytics (anonymized)",
        "Technical logs for security monitoring"
      ]
    },
    {
      title: "What We DON'T Collect",
      items: [
        "Student personal information (addresses, phone numbers)",
        "Social media profiles or external accounts",
        "Browsing history outside of exams",
        "Unnecessary personal details",
        "Data from inactive accounts"
      ]
    },
    {
      title: "How We Protect Data",
      items: [
        "AES-256 encryption for all data",
        "Regular security audits and penetration testing",
        "Employee background checks and training",
        "Incident response and breach notification procedures",
        "Regular security updates and monitoring"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              🔒 Security & Privacy First
            </h1>
            <p className="text-xl text-red-100 mb-8 leading-relaxed">
              Your students' data is precious. We use military-grade security, comply with all 
              education privacy laws, and give you complete control over your information.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white bg-opacity-20 px-6 py-3 rounded-lg">
                <div className="font-bold">🇪🇺 GDPR Compliant</div>
              </div>
              <div className="bg-white bg-opacity-20 px-6 py-3 rounded-lg">
                <div className="font-bold">🇺🇸 COPPA Certified</div>
              </div>
              <div className="bg-white bg-opacity-20 px-6 py-3 rounded-lg">
                <div className="font-bold">🏫 FERPA Compliant</div>
              </div>
              <div className="bg-white bg-opacity-20 px-6 py-3 rounded-lg">
                <div className="font-bold">✅ SOC 2 Audited</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Security Features */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">🛡️ Enterprise-Grade Security</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The same security standards used by banks and government agencies, 
              designed specifically for educational environments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8">
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{feature.description}</p>
                <ul className="space-y-3">
                  {feature.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-center text-gray-700">
                      <span className="text-green-500 mr-3">✅</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Compliance Section */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">📜 Regulatory Compliance</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We meet or exceed all major privacy regulations for educational technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {compliance.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  <h3 className="text-2xl font-bold text-gray-900 mr-3">{item.title}</h3>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                    {item.badge}
                  </span>
                </div>
                <p className="text-gray-700 mb-6">{item.description}</p>
                <ul className="space-y-3">
                  {item.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start text-gray-700">
                      <span className="text-green-500 mr-3 mt-0.5">✅</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data Handling */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">🔄 Data Lifecycle Management</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent, secure handling of your data from collection to deletion
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {dataHandling.map((stage, index) => (
              <div key={index} className="flex items-center mb-12 last:mb-0">
                <div className="flex-shrink-0 w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl mr-8">
                  {stage.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-2xl font-bold text-gray-900 mr-4">{stage.stage}</h3>
                    <span className="text-blue-600 font-semibold">{stage.description}</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{stage.details}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Privacy Details */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">🔍 Privacy Transparency</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete transparency about what data we collect and how we protect it
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {privacy.map((section, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{section.title}</h3>
                <ul className="space-y-4">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start text-gray-700">
                      <span className={`mr-3 mt-0.5 ${
                        section.title.includes("DON'T") ? 'text-red-500' : 'text-green-500'
                      }`}>
                        {section.title.includes("DON'T") ? '❌' : '✅'}
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust Signals */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-green-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">🏆 Trusted by Educators Worldwide</h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">🔒</div>
                <div className="text-2xl font-bold mb-2">Zero Breaches</div>
                <p className="text-blue-100">Perfect security record since launch</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">⏱️</div>
                <div className="text-2xl font-bold mb-2">24/7 Monitoring</div>
                <p className="text-blue-100">Continuous security monitoring and alerting</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">🛡️</div>
                <div className="text-2xl font-bold mb-2">99.9% Uptime</div>
                <p className="text-blue-100">Reliable, secure service you can depend on</p>
              </div>
            </div>

            <blockquote className="text-xl italic text-blue-100 mb-8">
              "Security isn't just a feature - it's the foundation of everything we do. 
              Your trust is our most valuable asset."
            </blockquote>

            <div className="space-y-4">
              <Link
                to="/teacher-register"
                className="inline-block bg-white text-blue-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition duration-200 shadow-lg mx-2"
              >
                🚀 Try ExamLock Securely
              </Link>
              <Link
                to="/contact"
                className="inline-block bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-600 transition duration-200 mx-2"
              >
                🛡️ Security Questions?
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Security Team */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">🔐 Security Questions or Concerns?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Our security team is available to answer any questions about our privacy practices, 
            compliance, or data protection measures.
          </p>
          <div className="space-y-4">
            <Link
              to="/contact"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition duration-200 shadow-lg mx-2"
            >
              📧 Contact Security Team
            </Link>
            <a
              href="/privacy-policy"
              className="inline-block bg-gray-600 hover:bg-gray-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition duration-200 shadow-lg mx-2"
            >
              📄 Read Full Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}